import React, { useState, useEffect } from 'react';
import { SERVER_BASE_URL } from '../../services/api';
import {
  Box,
  TextField,
  Typography,
  Avatar,
  IconButton,
  Grid,
  Card,
  CardContent,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { PhotoCamera, Link as LinkIcon } from '@mui/icons-material';
import { channelsAPI, playlistsAPI } from '../../services/api';

const BasicSettingsTab = ({ formData, onInputChange, playlistId }) => {
  const [iconUploading, setIconUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [sortOrderWarning, setSortOrderWarning] = useState('');
  const [allChannels, setAllChannels] = useState([]);

  useEffect(() => {
    loadAllChannels();
  }, []);

  const loadAllChannels = async () => {
    try {
      // Если канал редактируется в контексте плейлиста, загружаем каналы плейлиста
      const response = playlistId 
        ? await playlistsAPI.getChannels(playlistId)
        : await channelsAPI.getAll();
      setAllChannels(response.data);
    } catch (err) {
      console.error('Error loading channels:', err);
    }
  };

  const handleIconUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIconUploading(true);
    setUploadError('');

    try {
      const response = await channelsAPI.uploadIcon(file);
      onInputChange('icon', `${SERVER_BASE_URL}${response.data.icon_url}`);
    } catch (err) {
      setUploadError('Ошибка загрузки иконки');
    } finally {
      setIconUploading(false);
    }
  };

  const handleSortOrderChange = (value) => {
    // Разрешаем пустое значение для ввода
    if (value === '' || value === null || value === undefined) {
      onInputChange('sort_order', '');
      setSortOrderWarning('');
      return;
    }
    
    const sortOrder = parseInt(value);
    
    // Проверяем, что введено валидное число
    if (isNaN(sortOrder) || sortOrder < 1) {
      // Не обновляем значение, если введено невалидное число
      return;
    }
    
    onInputChange('sort_order', sortOrder);
    
    // Проверяем на дублирование
    // sortOrder из UI (1-based), но allChannels содержит 0-based значения из API
    // Поэтому конвертируем UI значение в 0-based для сравнения
    const sortOrderForComparison = sortOrder - 1;
    const duplicateChannel = allChannels.find(channel => 
      channel.sort_order === sortOrderForComparison && channel.id !== formData.id
    );
    
    if (duplicateChannel) {
      setSortOrderWarning(`Канал "${duplicateChannel.name}" уже использует порядок ${sortOrder}. При сохранении он будет отображаться рядом, но приоритет будет у канала с меньшим ID.`);
    } else {
      setSortOrderWarning('');
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Основная информация
      </Typography>

      <Grid container spacing={3}>
        {/* Иконка канала */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar
                src={formData.icon}
                sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
              >
                {formData.name.charAt(0)}
              </Avatar>
              
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="icon-upload"
                type="file"
                onChange={handleIconUpload}
                disabled={iconUploading}
              />
              <label htmlFor="icon-upload">
                <IconButton 
                  color="primary" 
                  component="span"
                  disabled={iconUploading}
                >
                  <PhotoCamera />
                </IconButton>
              </label>
              
              <Typography variant="caption" display="block">
                {iconUploading ? 'Загрузка...' : 'Загрузить иконку'}
              </Typography>
              
              {uploadError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {uploadError}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Основные поля */}
        <Grid item xs={12} md={8}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="URL канала"
              value={formData.url}
              onChange={(e) => onInputChange('url', e.target.value)}
              required
              helperText="Уникальный идентификатор канала (например: first-channel)"
              InputProps={{
                startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            
            <TextField
              fullWidth
              label="Название канала"
              value={formData.name}
              onChange={(e) => onInputChange('name', e.target.value)}
              required
              helperText="Отображаемое название канала"
            />
            
            <TextField
              fullWidth
              label="Ссылка на иконку"
              value={formData.icon}
              onChange={(e) => onInputChange('icon', e.target.value)}
              helperText="Прямая ссылка на изображение иконки"
              placeholder="https://example.com/icon.png"
            />
            
            <TextField
              fullWidth
              label="Порядок сортировки"
              type="number"
              value={formData.sort_order}
              onChange={(e) => handleSortOrderChange(e.target.value)}
              required
              helperText="Порядок отображения канала в списке (меньше = выше)"
              inputProps={{ min: 1, step: 1 }}
            />
            
            {sortOrderWarning && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {sortOrderWarning}
              </Alert>
            )}

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="status-label">Статус канала</InputLabel>
              <Select
                labelId="status-label"
                id="status-select"
                value={formData.is_active}
                label="Статус канала"
                onChange={(e) => onInputChange('is_active', e.target.value)}
              >
                <MenuItem value={true}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                    Активен
                  </Box>
                </MenuItem>
                <MenuItem value={false}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                    Отключен
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Grid>
      </Grid>

      {/* Предварительный просмотр */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Предварительный просмотр
        </Typography>
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar src={formData.icon} sx={{ width: 48, height: 48 }}>
                {formData.name.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {formData.name || 'Название канала'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formData.url || 'url-канала'}
                </Typography>
                <Typography variant="caption" color={formData.is_active ? 'success.main' : 'error.main'}>
                  {formData.is_active ? '● Активен' : '● Неактивен'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default BasicSettingsTab;
